import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ListChecks, MagnifyingGlass, Plus, ArrowsClockwise, Coins,
  FloppyDisk, Trash, ArrowCounterClockwise, Sparkle, Check, X,
  Notepad, Globe,
} from "@phosphor-icons/react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { RecipeSyncPanel, OnlineSyncPanel } from "../components/IngredientSyncPanels";
import { api } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const SOURCE_LABEL = {
  curated: { text: "Curated", cls: "bg-[#FDF9EE] border-[#D4AF37] text-[#A87C0E]" },
  override: { text: "Overridden", cls: "bg-emerald-50 border-emerald-500 text-emerald-700" },
  custom:   { text: "Custom",     cls: "bg-blue-50 border-blue-500 text-blue-700" },
};

export default function IngredientsMasterPage() {
  const { isAdmin, openLogin } = useAuth();
  const [payload, setPayload] = useState({ items: [], static_count: 0, override_count: 0, custom_count: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  // local draft rates: { rowKey: string }
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const rowKey = (it) => it.is_custom ? `c:${it.id}` : `b:${it.name}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ingredients/master");
      setPayload(data);
      setDrafts({});
    } catch { toast.error("Failed to load ingredients master"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    load();
  }, [isAdmin, load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (payload.items || []).filter((it) => {
      if (catFilter !== "All" && it.category !== catFilter.toLowerCase()) return false;
      if (sourceFilter !== "All" && it.source !== sourceFilter.toLowerCase()) return false;
      if (!needle) return true;
      const hay = `${it.name} ${(it.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [payload.items, q, catFilter, sourceFilter]);

  const setDraft = (it, val) => setDrafts((d) => ({ ...d, [rowKey(it)]: val }));
  const clearDraft = (it) => setDrafts((d) => { const n = { ...d }; delete n[rowKey(it)]; return n; });

  const saveRow = async (it) => {
    const k = rowKey(it);
    const draft = drafts[k];
    const newRate = parseFloat(draft);
    if (Number.isNaN(newRate) || newRate < 0) { toast.error("Enter a non-negative rate"); return; }
    setSavingKey(k);
    try {
      await api.put("/ingredients/master/rate", { name: it.name, rate_per_kg: newRate });
      toast.success(`${it.name} rate set to ₹${newRate.toFixed(2)}/kg`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingKey(null); }
  };

  const revertRow = async (it) => {
    if (!it.is_override) return;
    setSavingKey(rowKey(it));
    try {
      await api.delete(`/ingredients/master/override/${encodeURIComponent(it.name)}`);
      toast.success(`${it.name} reverted to default ₹${it.default_rate_per_kg.toFixed(2)}/kg`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revert failed"); }
    finally { setSavingKey(null); }
  };

  const deleteCustom = async (it) => {
    if (!window.confirm(`Delete custom ingredient "${it.name}"? Recipes using it will keep their stored rate.`)) return;
    setSavingKey(rowKey(it));
    try {
      await api.delete(`/ingredients/custom/${it.id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
    finally { setSavingKey(null); }
  };

  // ---- Add new ingredient ----
  const [adding, setAdding] = useState(false);
  const [newIng, setNewIng] = useState({ name: "", category: "primary", rate_per_kg: "", unit: "kg" });
  const [activeTab, setActiveTab] = useState("catalog"); // 'catalog' | 'recipes' | 'online'
  const submitNew = async () => {
    const name = newIng.name.trim();
    if (!name) { toast.error("Name required"); return; }
    const rate = parseFloat(newIng.rate_per_kg);
    if (Number.isNaN(rate) || rate < 0) { toast.error("Rate must be a non-negative number"); return; }
    setAdding(true);
    try {
      await api.post("/ingredients/custom", {
        name, category: newIng.category, rate_per_kg: rate, unit: newIng.unit, tags: ["custom"],
      });
      toast.success(`Added "${name}"`);
      setNewIng({ name: "", category: "primary", rate_per_kg: "", unit: "kg" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setAdding(false); }
  };

  // ---- Guest gate ----
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <ListChecks size={14} weight="fill" /> Ingredients Master
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              One source of truth <br className="hidden md:block" />
              <span className="text-[#D4AF37]">for every ingredient rate.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Set a rate once — it flows live into every recipe, menu and production plan.
            </p>
            <Button onClick={() => openLogin()} className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
                    data-testid="master-guest-login-btn">
              Admin login to manage rates
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Header />

      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-12">
          <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
            <ListChecks size={14} weight="fill" /> Ingredients Master · Live Source of Truth
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-medium mt-2 leading-tight">
            Edit rates here — <span className="text-[#D4AF37]">every recipe instantly recosts.</span>
          </h1>
          <p className="text-white/70 text-sm mt-2 max-w-3xl">
            Curated rates from Blinkit / Zepto / BigBasket are pre-loaded. Override any rate or
            add your own — all recipes, menus and production plans use the latest figures.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Stat label="Curated" value={payload.static_count} />
            <Stat label="Overridden" value={payload.override_count} accent="emerald" />
            <Stat label="Custom" value={payload.custom_count} accent="blue" />
            <Stat label="Total" value={(payload.items || []).length} accent="gold" />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-14 space-y-5">
        {/* Sub-tab switcher */}
        <div className="kostra-card p-1.5 inline-flex gap-1 self-start" data-testid="master-tabs">
          {[
            { key: "catalog", label: "Catalog", icon: <ListChecks size={14} weight="bold" /> },
            { key: "recipes", label: "Sync from My Recipes", icon: <Notepad size={14} weight="bold" /> },
            { key: "online",  label: "Sync from Online", icon: <Globe size={14} weight="bold" /> },
          ].map((t) => (
            <button key={t.key} type="button"
                    onClick={() => setActiveTab(t.key)}
                    className={`text-xs px-3 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 transition ${
                      activeTab === t.key
                        ? "bg-[#0A3B24] text-white"
                        : "text-[#0A3B24] hover:bg-[#FDF9EE]"}`}
                    data-testid={`master-tab-${t.key}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {activeTab === "recipes" && (
          <RecipeSyncPanel onApplied={load} />
        )}
        {activeTab === "online" && (
          <OnlineSyncPanel onApplied={load} masterItems={payload.items} />
        )}
        {activeTab === "catalog" && <>
        {/* Add new */}
        <div className="kostra-card p-5" data-testid="master-add-card">
          <h2 className="kostra-section-title text-lg flex items-center gap-2 mb-3">
            <Plus size={16} weight="duotone" className="text-[#D4AF37]" />
            Add Custom Ingredient
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <Label className="kostra-data-label mb-1.5 block">Name *</Label>
              <Input value={newIng.name}
                     onChange={(e) => setNewIng({ ...newIng, name: e.target.value })}
                     placeholder="e.g. House-blend Garam Masala"
                     className="h-11" data-testid="master-new-name" />
            </div>
            <div className="md:col-span-2">
              <Label className="kostra-data-label mb-1.5 block">Category</Label>
              <Select value={newIng.category} onValueChange={(v) => setNewIng({ ...newIng, category: v })}>
                <SelectTrigger className="h-11" data-testid="master-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="kostra-data-label mb-1.5 block">Rate (₹/kg) *</Label>
              <Input type="number" min="0" step="0.5"
                     value={newIng.rate_per_kg}
                     onChange={(e) => setNewIng({ ...newIng, rate_per_kg: e.target.value })}
                     placeholder="e.g. 450"
                     className="h-11 font-mono-num" data-testid="master-new-rate" />
            </div>
            <div className="md:col-span-1">
              <Label className="kostra-data-label mb-1.5 block">Unit</Label>
              <Input value={newIng.unit}
                     onChange={(e) => setNewIng({ ...newIng, unit: e.target.value })}
                     className="h-11" data-testid="master-new-unit" />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button onClick={submitNew} disabled={adding}
                      className="w-full h-11 bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold"
                      data-testid="master-new-submit">
                <Plus size={14} weight="bold" className="mr-1.5" />
                {adding ? "Adding…" : "Add"}
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="kostra-card p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <Label className="kostra-data-label mb-1.5 block">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search by name or tag…"
                       className="h-11 pl-9" data-testid="master-search-input" />
              </div>
            </div>
            <div className="md:col-span-4">
              <Label className="kostra-data-label mb-1.5 block">Category</Label>
              <div className="flex gap-1.5" data-testid="master-category-filter">
                {["All", "Primary", "Secondary"].map((c) => (
                  <button key={c} type="button" onClick={() => setCatFilter(c)}
                          className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${
                            catFilter === c
                              ? "bg-[#0A3B24] text-white border-[#0A3B24]"
                              : "bg-white text-[#0A3B24] border-[#0A3B24]/30 hover:border-[#0A3B24]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-3">
              <Label className="kostra-data-label mb-1.5 block">Source</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="h-11" data-testid="master-source-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All sources</SelectItem>
                  <SelectItem value="Curated">Curated only</SelectItem>
                  <SelectItem value="Override">Overridden only</SelectItem>
                  <SelectItem value="Custom">Custom only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="kostra-card p-0 overflow-hidden">
          <div className="px-5 py-3 flex items-center justify-between border-b border-[#E5E7EB]">
            <h2 className="kostra-section-title text-base flex items-center gap-2">
              <ListChecks size={14} weight="duotone" className="text-[#D4AF37]" />
              {filtered.length} ingredient{filtered.length === 1 ? "" : "s"}
            </h2>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
                    className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                    data-testid="master-refresh-btn">
              <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
              Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="master-table">
              <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#4B5563]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-bold">Name</th>
                  <th className="text-left px-3 py-2.5 font-bold">Category</th>
                  <th className="text-left px-3 py-2.5 font-bold">Tags</th>
                  <th className="text-right px-3 py-2.5 font-bold">Default ₹/kg</th>
                  <th className="text-right px-3 py-2.5 font-bold">Current ₹/kg</th>
                  <th className="text-center px-3 py-2.5 font-bold">Source</th>
                  <th className="text-right px-4 py-2.5 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-[#4B5563]">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-[#4B5563]">
                    No ingredients match your filters.
                  </td></tr>
                )}
                {filtered.map((it) => {
                  const k = rowKey(it);
                  const draft = drafts[k];
                  const editing = draft !== undefined;
                  const dirty = editing && parseFloat(draft) !== it.rate_per_kg;
                  const src = SOURCE_LABEL[it.source] || SOURCE_LABEL.curated;
                  const isSaving = savingKey === k;
                  return (
                    <tr key={k}
                        className="border-t border-[#E5E7EB] hover:bg-[#FDF9EE]/50 transition"
                        data-testid={`master-row-${it.name.replace(/\s+/g, "-").toLowerCase()}`}>
                      <td className="px-4 py-2.5 font-display text-[#0A3B24]">{it.name}</td>
                      <td className="px-3 py-2.5 capitalize text-[#4B5563]">{it.category}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(it.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] text-[#4B5563]">
                              {t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-[#4B5563]">
                        ₹{Number(it.default_rate_per_kg || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Input
                          type="number" min="0" step="0.5"
                          value={editing ? draft : it.rate_per_kg}
                          onChange={(e) => setDraft(it, e.target.value)}
                          className={`h-9 w-28 ml-auto font-mono-num text-right ${
                            dirty ? "border-[#D4AF37] bg-[#FDF9EE]" : ""}`}
                          data-testid={`master-rate-input-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border-2 font-bold ${src.cls}`}>
                          {src.text}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex gap-1.5">
                          {dirty && (
                            <>
                              <button type="button" onClick={() => saveRow(it)} disabled={isSaving}
                                      className="h-9 px-2.5 rounded-md bg-[#0A3B24] text-white text-xs font-semibold hover:bg-[#0E5232] inline-flex items-center gap-1 disabled:opacity-50"
                                      data-testid={`master-save-${it.name.replace(/\s+/g, "-").toLowerCase()}`}>
                                <Check size={12} weight="bold" /> Save
                              </button>
                              <button type="button" onClick={() => clearDraft(it)}
                                      className="h-9 px-2 rounded-md text-[#4B5563] text-xs font-semibold hover:bg-[#F9FAFB] inline-flex items-center"
                                      data-testid={`master-cancel-${it.name.replace(/\s+/g, "-").toLowerCase()}`}>
                                <X size={12} />
                              </button>
                            </>
                          )}
                          {!dirty && it.is_override && (
                            <button type="button" onClick={() => revertRow(it)} disabled={isSaving}
                                    className="h-9 px-2 rounded-md border border-[#0A3B24]/30 text-[#0A3B24] text-xs font-semibold hover:bg-[#FDF9EE] inline-flex items-center gap-1"
                                    title="Revert to curated default"
                                    data-testid={`master-revert-${it.name.replace(/\s+/g, "-").toLowerCase()}`}>
                              <ArrowCounterClockwise size={12} weight="bold" /> Revert
                            </button>
                          )}
                          {!dirty && it.is_custom && (
                            <button type="button" onClick={() => deleteCustom(it)} disabled={isSaving}
                                    className="h-9 px-2 rounded-md text-[#DC2626] text-xs font-semibold hover:bg-[#FBE8E8] inline-flex items-center"
                                    title="Delete custom ingredient"
                                    data-testid={`master-delete-${it.id}`}>
                              <Trash size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Hint */}
        <div className="text-xs text-[#4B5563] flex items-start gap-2 px-1">
          <Sparkle size={12} weight="fill" className="text-[#D4AF37] mt-0.5 shrink-0" />
          <span>
            Rate changes are <strong className="text-[#0A3B24]">live</strong> — every recipe, menu and production plan
            that uses an ingredient picks up its new rate instantly. Recipes where you manually overrode a rate
            in the Calculator (toggle on the ingredient row) keep their stored rate.
          </span>
        </div>
        </>}
      </main>

      <Footer />
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "gold" ? "text-[#D4AF37]"
    : accent === "emerald" ? "text-emerald-300"
    : accent === "blue" ? "text-blue-300" : "text-white";
  return (
    <div className="rounded-md bg-white/10 border border-white/20 px-3 py-1.5">
      <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{label}</span>
      <span className={`ml-2 font-mono-num font-bold ${cls}`}>{value}</span>
    </div>
  );
}
